import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { ReadinessCta } from "./ReadinessCta";

const HERO_IMG = "/globus-cover.png";

const lines = [
  { text: "Debt, structured.", accent: false },
  { text: "Growth, financed.", accent: false },
  { text: "Ambitions, funded.", accent: true },
];

const stats = [
  { value: "10", label: "Loan categories" },
  { value: "2", label: "Corporate + retail desks" },
  { value: "1–2 days", label: "Enquiry response time" },
  { value: "Pan-India", label: "Lender network" },
];

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Modern glass architecture" className="w-full h-[115%] object-cover" />
        <div className="absolute inset-0 bg-night/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-night via-night/85 to-[#0E7A5B]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-night via-transparent to-night/60" />
      </motion.div>

      <div className="relative z-10 flex-1 flex items-center max-w-7xl mx-auto w-full px-6 lg:px-10 pt-32 pb-16">
        <div className="max-w-4xl">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
            className="overline-label mb-8"
            data-testid="hero-overline"
          >
            Corporate Debt Advisory · Retail Lending
          </motion.p>

          <h1 data-testid="hero-headline" className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
            {lines.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "text-gold italic" : "text-foreground"}`}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1, ease, delay: 0.35 + i * 0.16 }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 1.0 }}
            className="mt-8 max-w-xl text-base md:text-lg text-[#A0AAB2] leading-relaxed"
            data-testid="hero-subtext"
          >
            From term loans and NCDs for corporates to home, business, LAP and gold loans
            for individuals — one advisor who negotiates on your side of the table.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 1.1 }}
            className="mt-10"
          >
            <ReadinessCta testId="hero-readiness-cta" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 1.25 }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <a
              data-testid="hero-enquire-button"
              href="#enquire"
              className="bg-gold text-night hover:bg-goldhover transition-colors rounded-sm px-7 py-3.5 font-semibold flex items-center gap-2"
            >
              Start an enquiry <ArrowUpRight size={16} />
            </a>
            <a
              data-testid="hero-calculator-button"
              href="#calculators"
              className="border border-gold text-gold hover:bg-gold/10 transition-colors rounded-sm px-7 py-3.5 font-medium"
            >
              Check your eligibility
            </a>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="relative z-10 border-t border-white/10 bg-night/40 backdrop-blur-sm"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
              <p className="font-serif text-2xl md:text-3xl text-gold">{s.value}</p>
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-28 right-8 hidden lg:block text-gold/60"
      >
        <ArrowDown size={20} />
      </motion.div>
    </section>
  );
};
